import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, HelpCircle, Images, Loader2, PencilLine, RefreshCw, Search, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Galeria, ObrazekTowaru } from '@/components/ObrazekTowaru';
import { NaprawObrazek } from '@/components/NaprawObrazek';
import { PodgladZMaszyny } from '@/components/PodgladZMaszyny';
import { SynchronizujObrazki } from '@/components/SynchronizujObrazki';
import { ZgrajZAutomatu } from '@/components/ZgrajZAutomatu';
import { Button } from '@/components/ui/button';
import { Tabs } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import {
  fetchPokrycieObrazkow,
  listaObrazkow,
  zgrajObrazkiZAutomatu,
  zlecSpisObrazkow,
  type Pokrycie,
} from '@/lib/items-api';
import { mozliwaNaprawaZMaszyny } from '@/lib/zrodla-obrazka';
import { cn } from '@/lib/utils';

/**
 * Po tylu minutach wynik sprawdzenia automatów uznajemy za nieaktualny i okno sprawdza je samo.
 * Pół godziny, bo zdjęcia zmieniają się rzadko, a sprawdzanie to sam odczyt katalogów.
 */
const SPIS_WAZNY_MIN = 30;

/**
 * ZARZĄDZANIE ZDJĘCIAMI — galeria, pokrycie na maszynach i dwie czynności w jednym miejscu.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️ POWSTAŁO NA ZGŁOSZENIE (31.08.2026): „w miejscu przycisku «Zgraj obrazki z automatu» robimy
 *  «Zarządzaj zdjęciami», a obecny przycisk będzie wewnątrz tego okienka. Powinno dać możliwość
 *  obejrzenia galerii oraz zobaczenia czy każda maszyna ma dla danego artykułu już poprawny
 *  obrazek, lub czy nie ma go wcale".
 *
 *  ⚠️⚠️ DWIE RÓŻNE CZYNNOŚCI, KTÓRYCH NIE WOLNO ZLAĆ W JEDEN PRZYCISK:
 *
 *    SPRAWDŹ NA MASZYNACH (`OBRAZY_SPIS`) — czyta katalog grafik na KAŻDEJ maszynie i nie
 *    przenosi ani jednego bajtu. Stąd bierze się odpowiedź „ma / nie ma".
 *
 *    ZGRAJ Z AUTOMATU (`OBRAZY_ZGRAJ`) — POBIERA pliki do biblioteki portalu, z JEDNEGO węzła
 *    i tylko te, których jeszcze nie mamy. To uzupełnianie biblioteki, nie pomiar.
 *
 *  Jeden przycisk robiący oba znaczyłby przy stu grafikach na dziesięciu maszynach setki megabajtów
 *  ruchu za każde otwarcie okna — dokładnie tam, gdzie mamy alarm progu ruchu agenta.
 *
 *  ⚠️ CHMUROWY MASTER NIE JEST KOLUMNĄ w tej tabeli, i to jest wymóg ze zgłoszenia. Węzeł bez
 *  automatu nie ma katalogu grafik ani ekranu — odsiewa go instancja (`pokrycieObrazkow`), więc
 *  ekran nie ma tu żadnej własnej reguły do złamania.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

/** Ile wierszy rysujemy bez filtra — pełna lista towarów u klienta to setki pozycji. */
const WIERSZY_NA_RAZ = 60;

/**
 * ⚠️⚠️ DWA KOLORY TO DWIE RÓŻNE REAKCJE — wymóg wprost ze zgłoszenia (31.08.2026): „powinno być
 * czerwone pole jak nie ma obrazka wcale, ale żółte jak jest ale jakiś inny niż zakładany".
 * Czerwone trzeba UZUPEŁNIĆ (pracownik widzi pustą ramkę), żółte trzeba OBEJRZEĆ (widzi obrazek,
 * ale nie ten). Zrównanie ich znaczyłoby, że technik jedzie wgrywać plik tam, gdzie plik jest.
 *
 * ⚠️ PUSTY PLIK JEST CZERWONY, nie żółty: plik zerowej długości to na ekranie automatu PUSTE
 * MIEJSCE, czyli dokładnie to samo, co brak pliku.
 */
const ZNACZNIK: Record<string, { ikona: typeof Check; klasa: string }> = {
  MA: { ikona: Check, klasa: 'text-[var(--success)]' },
  BRAK: { ikona: AlertTriangle, klasa: 'text-destructive' },
  PUSTY_PLIK: { ikona: AlertTriangle, klasa: 'text-destructive' },
  INNY_PLIK: { ikona: AlertTriangle, klasa: 'text-[var(--warning)]' },
  INNY_ROZMIAR: { ikona: AlertTriangle, klasa: 'text-[var(--warning)]' },
  NIEZMIERZONE: { ikona: HelpCircle, klasa: 'text-muted-foreground' },
};

export function ZarzadzanieZdjeciami({
  groupId,
  mozeZapisywac,
  onClose,
}: {
  groupId: string;
  mozeZapisywac: boolean;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const [szukaj, setSzukaj] = useState('');
  const [galeriaSzukaj, setGaleriaSzukaj] = useState('');
  const [wybrany, setWybrany] = useState<string | null>(null);
  /**
   * ZAKŁADKI: POKRYCIE i GALERIA.
   *
   * ⚠️⚠️ ZGŁOSZONE (31.08.2026): „galerię robimy jako osobna zakładka". Do tej pory stały obok
   * siebie w dwóch kolumnach — co było poprawą wobec „jedno pod drugim", ale i tak dzieliło
   * szerokość okna między tabelę pokrycia (towar + plik + kolumna na automat) i galerię. Osobne
   * zakładki dają obu pełną szerokość, a przełączenie jest jednym kliknięciem.
   */
  const [zakladka, setZakladka] = useState<'pokrycie' | 'galeria'>('pokrycie');
  /**
   * ⚠️⚠️ ZGRYWANIE PRZEZ OKNO WYBORU, NIE „NA KLIK" — poprawka po PESIE: „maszyna, z której chciało
   * pobrać, nie miała żadnych obrazków, a pozostałe automaty miały i musi być wybór z jakiego
   * ciągniemy wszystko". Przycisk otwiera więc wybór źródła, a nie od razu zleca zadanie.
   */
  const [wybierzZrodlo, setWybierzZrodlo] = useState(false);
  /**
   * ⚠️⚠️ DRUGI KIERUNEK TEGO SAMEGO EKRANU. „Zgraj" ciągnie bibliotekę Z automatu do portalu,
   * „synchronizuj" rozsyła ją W DÓŁ na automaty, którym plików brakuje — zamówione razem
   * 07.09.2026. Oba przyciski stoją w zakładce galerii, bo oba dotyczą BIBLIOTEKI, a nie pokrycia.
   */
  const [synchronizuj, setSynchronizuj] = useState(false);
  /**
   * ⚠️ ID TOWARU, KTÓREMU ZMIENIAMY OBRAZEK — otwiera zwykłe okno obrazka towaru (galeria +
   * wgranie z dysku + rozesłanie na WSZYSTKIE automaty, razem z ostrzeżeniem o nadpisaniu).
   * Zgłoszone: „na liście opcja zmiany na coś z galerii i pushnięcia na wszystkie automaty" —
   * i to jest dokładnie to okno, więc nie budujemy drugiego.
   */
  const [zmieniany, setZmieniany] = useState<string | null>(null);
  /**
   * CO POKAZAĆ Z MASZYNY — `null` = nic nie otwarte.
   *
   * ⚠️⚠️ ZGŁOSZONE (31.08.2026): „gdy plik się nie zgadza, to możliwość wyświetlenia go". Wiersz
   * mówił „ta maszyna wskazuje inny plik" i nie dawał go zobaczyć — a wtedy jedyną drogą było
   * wejście na maszynę klienta.
   */
  const [podglad, setPodglad] = useState<{ agentId: string; maszyna: string; nodeId: string; plik: string } | null>(
    null,
  );
  /** Domyślnie pokazujemy WSZYSTKO; filtr „tylko braki" jest jednym kliknięciem. */
  const [tylkoBraki, setTylkoBraki] = useState(false);
  /**
   * WIERSZ, KTÓRY NAPRAWIAMY ŚCIĄGNIĘCIEM Z MASZYNY.
   *
   * ⚠️⚠️ ZAMÓWIONE 07.09.2026 po zrzucie z testówki PESA: „braki: 13", a przy każdym z tych
   * wierszy „portal nie ma tego pliku". TAKICH WIERSZY NIE DA SIĘ NAPRAWIĆ WYSŁANIEM — nie ma
   * po naszej stronie ani jednego bajtu. Jedyna droga prowadzi w drugą stronę.
   */
  const [naprawiany, setNaprawiany] = useState<string | null>(null);

  /**
   * ⚠️ NAZWY Z BIBLIOTEKI — do podpowiedzi nazwy przy zapisie pliku ściągniętego z maszyny. Bez nich
   * okno zapisu nie wie, czy nazwa jest wolna, i albo zawsze dokleja węzeł, albo zawsze nadpisuje.
   */
  const biblioteka = useQuery({
    queryKey: ['obrazki', groupId, '', 100],
    queryFn: () => listaObrazkow(groupId, '', 100),
  });

  const dane = useQuery({
    queryKey: ['pokrycie-obrazkow', groupId],
    queryFn: () => fetchPokrycieObrazkow(groupId),
    /**
     * ⚠️⚠️ ODŚWIEŻAMY SAMI, DOPÓKI SPRAWDZANIE TRWA — naprawa zgłoszenia „kliknąłem sprawdź na
     * maszynach ale w sumie to nic się nie zadziało". Spis wykonuje AGENT w swoim cyklu, więc wynik
     * przychodzi po minutach; bez tego okno wyglądało identycznie przed kliknięciem i po nim,
     * a jedynym śladem był dymek, który zdążył zniknąć.
     *
     * ⚠️ PYTAMY TYLKO W TRAKCIE. Odpytywanie co 10 s bez powodu obciążałoby instancję przejściem
     * całego planogramu wyspy — najdroższym odczytem, jaki ma ten ekran.
     */
    refetchInterval: (q) => (q.state.data?.maszyny.some((m) => m.wToku) === true ? 10_000 : false),
  });

  const spis = useMutation({
    mutationFn: () => zlecSpisObrazkow(groupId),
    onSuccess: (r) => {
      /**
       * ⚠️ MÓWIMY, ŻE WYNIK PRZYJDZIE PÓŹNIEJ. Zadanie idzie do agenta i wraca w jego cyklu, więc
       * tabela nie odświeży się „na klik" — obiecywanie tego dawałoby wrażenie, że pomiar nie działa.
       * Postęp widać teraz przy maszynach jako „sprawdzanie w toku", a okno samo się odświeża.
       *
       * ⚠️ DWA RÓŻNE POWODY ZERA, i poprzednia wersja zlewała je w jeden nieprawdziwy komunikat
       * („żadna maszyna nie ma agenta w wersji, która to potrafi") — także wtedy, gdy przyczyna
       * była inna. Teraz o wersji mówimy TYLKO wtedy, gdy naprawdę o nią chodzi, i podajemy
       * wersje, na które trzeba poczekać.
       */
      if (r.zadan > 0) {
        toast.success(t('zdj.spisZlecony').replace('{zadan}', String(r.zadan)).replace('{wezlow}', String(r.wezlow)));
      } else if (r.zaStare.length > 0) {
        toast.error(
          t('zdj.spisZaStareWszystkie')
            .replace('{wezly}', r.zaStare.join(', '))
            .replace('{wersja}', r.wersjaWymagana),
        );
      } else {
        toast.error(t('zdj.spisNikt'));
      }
      if (r.zadan > 0 && r.zaStare.length > 0) {
        toast.error(
          t('zdj.spisZaStare').replace('{wezly}', r.zaStare.join(', ')).replace('{wersja}', r.wersjaWymagana),
        );
      }
      void qc.invalidateQueries({ queryKey: ['pokrycie-obrazkow'] });
    },
    onError: (e: Error) => toast.error(e instanceof ApiError ? e.message : String(e)),
  });

  const zgraj = useMutation({
    mutationFn: (agentId: string) => zgrajObrazkiZAutomatu(groupId, agentId),
    onSuccess: (r) => {
      setWybierzZrodlo(false);
      toast.success(
        r.zadan === 0
          ? t('obraz.zgraneNicNowego').replace('{n}', String(r.juzMamy))
          : t('obraz.zgraneZlecone')
              .replace('{zadan}', String(r.zadan))
              .replace('{plikow}', String(r.plikow))
              .replace('{wezel}', r.wezel),
      );
      void qc.invalidateQueries({ queryKey: ['obrazki'] });
      void qc.invalidateQueries({ queryKey: ['pokrycie-obrazkow'] });
    },
    onError: (e: Error) => toast.error(e instanceof ApiError ? e.message : String(e)),
  });

  const d: Pokrycie | undefined = dane.data;

  /**
   * ══════════════════════════════════════════════════════════════════════════════════════════
   *  ⚠️⚠️ SPRAWDZANIE RUSZA SAMO PRZY OTWARCIU, gdy ostatnie jest nieaktualne — zamówione 24.09.2026.
   *  Pokrycie bez sprawdzenia nie odpowiada na nic („nie sprawdzano" przy każdym automacie), a przycisk
   *  trzeba było najpierw znaleźć i kliknąć. To sam odczyt katalogów — ani jeden plik nie jedzie —
   *  więc wolno go zlecić bez pytania; przycisk „Sprawdź automaty" zostaje na żądanie.
   *
   *  ⚠️ RAZ NA OTWARCIE (`useRef`), tylko dla admina i tylko wtedy, gdy nic już nie trwa — inaczej
   *  każde odświeżenie pokrycia zlecałoby kolejne sprawdzenie w pętli.
   * ══════════════════════════════════════════════════════════════════════════════════════════
   */
  const autoSpis = useRef(false);
  useEffect(() => {
    if (autoSpis.current || !mozeZapisywac || d === undefined || spis.isPending) return;
    const umieja = d.maszyny.filter((m) => m.umieSpis);
    if (umieja.length === 0 || umieja.some((m) => m.wToku)) return;
    const nieaktualne = umieja.some(
      (m) => m.spisAt === null || Date.now() - new Date(m.spisAt).getTime() > SPIS_WAZNY_MIN * 60_000,
    );
    if (!nieaktualne) return;
    autoSpis.current = true;
    spis.mutate();
  }, [d, mozeZapisywac, spis]);
  const doZmiany = zmieniany === null ? null : (d?.pokrycie ?? []).find((p) => p.towar.id === zmieniany) ?? null;
  const doNaprawy = naprawiany === null ? null : (d?.pokrycie ?? []).find((p) => p.towar.id === naprawiany) ?? null;
  const fraza = szukaj.trim().toLowerCase();
  const wiersze = (d?.pokrycie ?? []).filter((p) => {
    if (tylkoBraki && p.stan !== 'BRAKI') return false;
    if (fraza === '') return true;
    return (
      p.towar.nazwa.toLowerCase().includes(fraza) ||
      p.towar.itemNumber.toLowerCase().includes(fraza) ||
      (p.towar.imageFile ?? '').toLowerCase().includes(fraza)
    );
  });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      {/*
        ⚠️⚠️ `w-[…]`, NIE `max-w-[…]`, i to jest naprawa zgłoszenia „okienko z obrazkami jest za
        wąskie". Bazowa klasa `DialogContent` ustawia SZEROKOŚĆ (`w-[min(32rem,…)]`), a nie jej
        górną granicę — `max-w-5xl` nic więc nie robił, bo `max-w` tylko ogranicza, a element i tak
        stał na sztywnych 32 rem. `twMerge` podmienia klasę tej samej rodziny, więc wystarczy podać
        `w-` po swojemu.
      */}
      <DialogContent className="w-[min(78rem,calc(100vw-2rem))]" title={t('zdj.tytul')}>
        <div className="space-y-4">
          <Tabs
            zakladki={[
              { id: 'pokrycie', etykieta: t('zdj.zakladka.pokrycie'), licznik: d?.podsumowanie.BRAKI ?? null },
              { id: 'galeria', etykieta: t('zdj.zakladka.galeria') },
            ]}
            wybrana={zakladka}
            onWybor={setZakladka}
          />

          {/* ── CZYNNOŚCI ─────────────────────────────────────────────────────── */}
          {/*
            ⚠️ CZYNNOŚĆ IDZIE ZA ZAKŁADKĄ. „Sprawdź na maszynach" dotyczy POKRYCIA (pyta automaty,
            co u nich leży), a „Zgraj obrazki z automatu" napełnia BIBLIOTEKĘ — czyli galerię.
            Trzymanie obu nad zakładkami znaczyłoby dwa przyciski, z których jeden w danym widoku
            nic nie zmienia.
          */}
          <div className="flex flex-wrap items-center gap-2">
            {zakladka === 'pokrycie' ? (
              <Button
                size="sm"
                variant="outline"
                disabled={!mozeZapisywac || spis.isPending}
                title={t('zdj.spisOpis')}
                onClick={() => spis.mutate()}
              >
                {spis.isPending ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                {t('zdj.spis')}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                disabled={!mozeZapisywac || zgraj.isPending || d === undefined || d.maszyny.length === 0}
                title={t('obraz.zgrajOpis')}
                onClick={() => setWybierzZrodlo(true)}
              >
                {zgraj.isPending ? <Loader2 className="size-4 animate-spin" /> : <Images className="size-4" />}
                {t('obraz.zgraj')}
              </Button>
            )}
            {/*
              ⚠️⚠️ SYNCHRONIZACJA STOI OBOK ZGRYWANIA I TO JEST TREŚĆ, NIE UKŁAD: te dwa przyciski
              to dwa kierunki tej samej biblioteki — „zgraj" bierze pliki Z automatu, „synchronizuj"
              rozsyła je NA automaty, którym ich brakuje. Rozdzielenie ich po ekranach kazałoby
              szukać drugiej połowy czynności gdzie indziej.
            */}
            {zakladka === 'galeria' && (
              <Button
                size="sm"
                variant="outline"
                disabled={!mozeZapisywac || d === undefined || d.maszyny.length === 0}
                title={t('obraz.syn.opis')}
                onClick={() => setSynchronizuj(true)}
              >
                <Upload className="size-4" />
                {t('obraz.syn.przycisk')}
              </Button>
            )}
          </div>

          {/* ── PODSUMOWANIE ──────────────────────────────────────────────────── */}
          {zakladka === 'pokrycie' && d !== undefined && (
            <div className="flex flex-wrap gap-3 text-xs">
              {(
                [
                  ['WSZEDZIE', 'text-[var(--success)]'],
                  ['BRAKI', 'text-destructive'],
                  ['ROZJAZD', 'text-[var(--warning)]'],
                  ['NIEZMIERZONE', 'text-muted-foreground'],
                  ['BEZ_OBRAZKA', 'text-muted-foreground'],
                ] as const
              ).map(([k, klasa]) => (
                <span key={k} className={klasa}>
                  {t(`zdj.stan.${k}`)}: <span className="font-medium tabular-nums">{d.podsumowanie[k]}</span>
                </span>
              ))}
            </div>
          )}

          {/* ⚠️ KIEDY MIERZONO — bez tej daty „ma/nie ma" jest twierdzeniem bez czasu. */}
          {zakladka === 'pokrycie' && d !== undefined && d.maszyny.length > 0 && (
            <ul className="space-y-0.5 text-[11px] text-muted-foreground">
              {d.maszyny.map((m) => (
                <li key={m.agentId}>
                  <span className="font-medium text-foreground">{m.automat?.nazwa ?? m.nodeId}</span>
                  {' — '}
                  {!m.umieSpis ? (
                    t('zdj.wezelZaStary').replace('{wersja}', m.wersja ?? '—').replace('{wymagana}', d.wersjaWymagana)
                  ) : m.wToku ? (
                    /** ⚠️ „W TOKU" PRZED RESZTĄ — to ono odpowiada na „czy coś się dzieje". */
                    t('zdj.wToku')
                  ) : m.ostatniaProba !== null && m.ostatniaProba.stan === 'FAILED' ? (
                    /**
                     * ⚠️⚠️ PORAŻKA Z TREŚCIĄ, i to jest sedno tej poprawki. Bez niej zadanie nieudane
                     * wypadało z widoku i wiersz wracał do „nie sprawdzano" — czyli po kliknięciu
                     * człowiek widział TO SAMO co przed nim, a powód leżał w bazie.
                     */
                    <span className="text-destructive">
                      {t('zdj.probaNieudana')
                        .replace('{kiedy}', new Date(m.ostatniaProba.kiedy).toLocaleString())
                        .replace('{powod}', m.ostatniaProba.blad ?? m.ostatniaProba.note ?? '—')}
                    </span>
                  ) : m.spisAt === null ? (
                    t('zdj.nieMierzono')
                  ) : (
                    t('zdj.mierzono').replace('{kiedy}', new Date(m.spisAt).toLocaleString())
                  )}
                </li>
              ))}
            </ul>
          )}

          {/*
            ⚠️⚠️ POKRYCIE I GALERIA OBOK SIEBIE, NIE JEDNO POD DRUGIM — naprawa zgłoszenia „galeria
            jest w takim miejscu, że trzeba scrollować, żeby w ogóle tam dojechać". Pod sobą galeria
            lądowała poniżej tabeli na sześćdziesiąt wierszy, czyli poza ekranem: dwie rzeczy, które
            porównuje się WZAJEMNIE (czego brakuje na maszynach kontra co portal ma w bibliotece),
            wymagały przewijania w tę i z powrotem.

            ⚠️ NA WĘŻSZYM EKRANIE WRACA UKŁAD PIONOWY (`lg:`), bo dwie kolumny po 30 rem nie mieszczą
            się na laptopie 13" — a wtedy gorsze niż przewijanie jest ucięcie tabeli w połowie.
          */}
          <div className="grid gap-4">
            <div className={cn('min-w-0 space-y-3', zakladka === 'pokrycie' ? '' : 'hidden')}>
          {/* ── FILTRY ────────────────────────────────────────────────────────── */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-56 flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-8"
                autoComplete="off"
                placeholder={t('zdj.szukaj')}
                value={szukaj}
                onChange={(e) => setSzukaj(e.target.value)}
              />
            </div>
            <Button size="sm" variant={tylkoBraki ? 'default' : 'outline'} onClick={() => setTylkoBraki((w) => !w)}>
              {t('zdj.tylkoBraki')}
            </Button>
          </div>

          {/* ── POKRYCIE ──────────────────────────────────────────────────────── */}
          <div className="max-h-[26rem] overflow-auto rounded-md border border-border">
            {dane.isPending && <p className="p-3 text-xs text-muted-foreground">{t('common.loading')}</p>}
            {dane.isError && (
              <p className="p-3 text-xs text-destructive">
                {t('common.error')}: {(dane.error as Error).message}
              </p>
            )}
            {d !== undefined && d.maszyny.length === 0 && (
              /** ⚠️ Wyspa bez węzła przy automacie nie ma czego pokrywać — i to nie jest usterka. */
              <p className="p-3 text-xs text-muted-foreground">{t('zdj.brakMaszyn')}</p>
            )}
            {d !== undefined && d.maszyny.length > 0 && wiersze.length === 0 && (
              <p className="p-3 text-xs text-muted-foreground">{t('zdj.brakWynikow')}</p>
            )}
            {d !== undefined && d.maszyny.length > 0 && wiersze.length > 0 && (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-card">
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="p-2">{t('zdj.towar')}</th>
                    <th className="p-2">{t('zdj.plik')}</th>
                    {d.maszyny.map((m) => (
                      <th key={m.agentId} className="p-2 text-center">
                        {m.automat?.nazwa ?? m.nodeId}
                      </th>
                    ))}
                    <th className="p-2" />
                  </tr>
                </thead>
                <tbody>
                  {wiersze.slice(0, WIERSZY_NA_RAZ).map((p) => (
                    <tr key={p.towar.id} className="border-b border-border/60">
                      <td className="p-2">
                        <span className="block truncate">{p.towar.nazwa}</span>
                        <span className="font-mono text-[10px] text-muted-foreground">{p.towar.itemNumber}</span>
                      </td>
                      <td className="p-2">
                        {/*
                          ════════════════════════════════════════════════
                           NAZWA PLIKU JEST PRZYCISKIEM — zamówione 07.09.2026: „klikanie na plik obrazka
                           konkretny powinno otwierać małe okienko z galerii z listą do wyboru i możliwością
                           pusha na maszyny wszystkie".

                           ⚠️⚠️ OTWIERAMY OKNO, KTÓRE JUŻ BYŁO (to samo, co ołówek „Zmień" po prawej), a nie
                           drugie, własne — rozstrzygnięte wprost. Ono MA listę z galerii, wgranie z dysku,
                           push na wszystkie automaty i ostrzeżenie o nadpisaniu, czyli dokładnie to, co
                           w zamówieniu. Brakowało DROGI do niego, nie funkcji: przy trzech kolumnach
                           automatów ołówek stoi na drugim końcu wiersza i nic go z nazwą nie łączyło.
                          ════════════════════════════════════════════════
                        */}
                        {p.towar.imageFile === null || p.towar.imageFile.trim() === '' ? (
                          <button
                            type="button"
                            disabled={!mozeZapisywac}
                            title={t('zdj.zmienOpis')}
                            className="rounded text-left text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground disabled:cursor-not-allowed disabled:no-underline"
                            onClick={() => setZmieniany(p.towar.id)}
                          >
                            {t('zdj.bezPliku')}
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              disabled={!mozeZapisywac}
                              title={t('zdj.zmienOpis')}
                              className="rounded font-mono underline decoration-dotted underline-offset-2 hover:text-[var(--primary)] disabled:cursor-not-allowed disabled:no-underline"
                              onClick={() => setZmieniany(p.towar.id)}
                            >
                              {p.towar.imageFile}
                            </button>
                            {/*
                              ⚠️⚠️ BEZ BAJTÓW W PORTALU BRAKU NIE DA SIĘ NAPRAWIĆ WYSŁANIEM — i od 07.09.2026
                              nie zostawiamy tego jako samego zdania. Jeśli któraś maszyna ten plik MA,
                              dajemy tu KROK: ściągnij z niej i rozgłoś. To było 13 wierszy na testówce,
                              których żadnym przyciskiem w portalu nie dawało się ruszyć.
                            */}
                            {!p.wBibliotece &&
                              (mozliwaNaprawaZMaszyny(p, d.maszyny) && mozeZapisywac ? (
                                <button
                                  type="button"
                                  className="ml-2 text-[var(--warning)] underline decoration-dotted underline-offset-2 hover:text-foreground"
                                  onClick={() => setNaprawiany(p.towar.id)}
                                >
                                  {t('zdj.brakWBibliotece')} — {t('zdj.sciagnijZMaszyny')}
                                </button>
                              ) : (
                                <span className="ml-2 text-[var(--warning)]">{t('zdj.brakWBibliotece')}</span>
                              ))}
                          </>
                        )}
                      </td>
                      {d.maszyny.map((m) => {
                        const wpis = p.maszyny.find((x) => x.agentId === m.agentId);
                        const stan = wpis?.stan ?? 'NIEZMIERZONE';
                        if (p.stan === 'BEZ_OBRAZKA') return <td key={m.agentId} className="p-2 text-center">—</td>;
                        const { ikona: Ikona, klasa } = ZNACZNIK[stan]!;
                        /*
                          ⚠️ DYMEK MÓWI, CO KONKRETNIE JEST NIE TAK. „Inny obrazek" bez nazwy pliku
                          nie mówi, czego szukać na maszynie — a to jedyna rzecz, po którą się tam
                          jedzie. Przy rozjeździe rozmiaru podajemy liczbę bajtów, bo nazwa się
                          zgadza i to ona jest jedyną różnicą, jaką widzimy.
                        */
                        const dymek =
                          stan === 'INNY_PLIK' && wpis?.plikMaszyny !== null && wpis?.plikMaszyny !== undefined
                            ? t('zdj.stanMaszyny.INNY_PLIK.opis').replace('{plik}', wpis.plikMaszyny)
                            : stan === 'INNY_ROZMIAR' && wpis?.rozmiar !== null && wpis?.rozmiar !== undefined
                              ? t('zdj.stanMaszyny.INNY_ROZMIAR.opis').replace(
                                  '{bajty}',
                                  wpis.rozmiar.toLocaleString('pl-PL'),
                                )
                              : t(`zdj.stanMaszyny.${stan}`);
                        /**
                         * ⚠️ KOMÓRKA JEST KLIKALNA WTEDY, GDY JEST CO POKAZAĆ: plik na maszynie
                         * istnieje (`MA`, `INNY_PLIK`, `INNY_ROZMIAR`, `PUSTY_PLIK`). Przy `BRAK`
                         * i `NIEZMIERZONE` nie ma o co pytać — kliknięcie kończyłoby się zadaniem
                         * odrzuconym przez agenta i czerwonym wpisem w historii za nic.
                         *
                         * ⚠️ PYTAMY O PLIK, KTÓRY WIDZI TA MASZYNA (`plikMaszyny`), a nie o ten
                         * z mastera — cała wartość podglądu leży w tym, że pokazuje, co tam
                         * NAPRAWDĘ leży.
                         */
                        const plikNaMaszynie = wpis?.plikMaszyny ?? p.towar.imageFile;
                        const doPodgladu =
                          plikNaMaszynie !== null &&
                          (stan === 'MA' || stan === 'INNY_PLIK' || stan === 'INNY_ROZMIAR' || stan === 'PUSTY_PLIK');
                        return (
                          <td key={m.agentId} className="p-2 text-center">
                            {doPodgladu ? (
                              <button
                                type="button"
                                title={`${dymek} — ${t('zdj.pokazZMaszyny')}`}
                                className={cn(
                                  'inline-flex rounded p-0.5 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                  klasa,
                                )}
                                onClick={() =>
                                  setPodglad({
                                    agentId: m.agentId,
                                    maszyna: m.automat?.nazwa ?? m.nodeId,
                                    nodeId: m.nodeId,
                                    plik: plikNaMaszynie,
                                  })
                                }
                              >
                                <Ikona className="size-4" />
                              </button>
                            ) : (
                              <span title={dymek} className={cn('inline-flex', klasa)}>
                                <Ikona className="size-4" />
                              </span>
                            )}
                          </td>
                        );
                      })}
                      {/*
                        ⚠️ ZMIANA OBRAZKA WPROST Z TEJ LISTY (zgłoszone 31.08.2026: „na liście opcja
                        zmiany na coś z galerii i pushnięcia na wszystkie automaty"). Otwieramy
                        ZWYKŁE okno obrazka towaru — ma galerię, wgranie z dysku, rozesłanie na
                        wszystkie automaty i ostrzeżenie o nadpisaniu pliku, który tam już leży.
                        Drugie, „własne" okno znaczyłoby dwie drogi do tej samej zmiany.
                      */}
                      <td className="p-2 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-[11px]"
                          disabled={!mozeZapisywac}
                          title={t('zdj.zmienOpis')}
                          onClick={() => setZmieniany(p.towar.id)}
                        >
                          <PencilLine className="size-3.5" />
                          {t('zdj.zmien')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {wiersze.length > WIERSZY_NA_RAZ && (
            /** ⚠️ Mówimy, że lista jest UCIĘTA — inaczej „nie ma tego towaru" znaczyłoby dwie rzeczy. */
            <p className="text-xs text-muted-foreground">
              {t('zdj.ucieto').replace('{ile}', String(WIERSZY_NA_RAZ)).replace('{z}', String(wiersze.length))}
            </p>
          )}

            </div>

            {/* ── GALERIA ─────────────────────────────────────────────────────── */}
            {/*
              ⚠️ TU WOLNO USUWAĆ I ZMIENIAĆ NAZWY, w oknie przypisywania obrazka NIE. Porządkowanie
              biblioteki jest tym, po co się do tego okna przychodzi; przy przypisywaniu kasowanie
              z tej samej listy byłoby pomyłką o jeden wiersz.

              ⚠️ `zarzadzanie` włącza też szczegóły pliku (na ilu automatach leży, które towary go
              wskazują) i dodanie pliku z komputera — czyli to, po co ta zakładka istnieje.
            */}
            <div className={cn('min-w-0 space-y-2', zakladka === 'galeria' ? '' : 'hidden')}>
              <Galeria
                islandId={groupId}
                wybrany={wybrany}
                onWybierz={setWybrany}
                obecny={null}
                szukaj={galeriaSzukaj}
                onSzukaj={setGaleriaSzukaj}
                mozeUsuwac={mozeZapisywac}
                zarzadzanie
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/*
        ⚠️ OKNO W OKNIE, świadomie: to samo okno, którym zmienia się obrazek z listy towarów, więc
        zachowuje się identycznie (podgląd obecnego, galeria, wgranie z dysku, postęp na automatach).
        Po zamknięciu odświeżamy pokrycie — inaczej wiersz nadal pokazywałby stary stan.
      */}
      {podglad !== null && (
        <PodgladZMaszyny
          groupId={groupId}
          agentId={podglad.agentId}
          nazwaMaszyny={podglad.maszyna}
          nodeId={podglad.nodeId}
          nazwaPliku={podglad.plik}
          /**
           * ⚠️⚠️ NAZWY Z BIBLIOTEKI SĄ TU CAŁĄ RÓŻNICĄ MIĘDZY „zapisz" I „nadpisz". Bez nich okno
           * podglądu albo zawsze dokleja nazwę węzła, albo zawsze zamazuje plik, który mamy —
           * a to jedyna kopia poza automatami.
           */
          wBibliotece={(biblioteka.data?.pliki ?? []).map((x) => x.nazwaPliku)}
          onClose={() => {
            setPodglad(null);
            void dane.refetch();
          }}
        />
      )}

      {/*
        ⚠️⚠️ NAPRAWA WIERSZA, KTÓREGO NIE DA SIĘ NAPRAWIĆ WYSŁANIEM. Portal nie ma bajtów, więc
        push nie istnieje jako możliwość — okno prowadzi w drugą stronę: wybór maszyny, obejrzenie
        pliku, zapis do biblioteki i dopiero wtedy rozesłanie. Trzy kroki są celowe, bo pod jedną
        nazwą leżą na automatach RÓŻNE obrazki i to człowiek rozstrzyga, która wersja jest dobra.
      */}
      {doNaprawy !== null && d !== undefined && (
        <NaprawObrazek
          groupId={groupId}
          wiersz={doNaprawy}
          maszyny={d.maszyny}
          onClose={() => {
            setNaprawiany(null);
            void dane.refetch();
          }}
        />
      )}

      {/*
        ⚠️⚠️ WYBÓR ŹRÓDŁA JAKO OSOBNY KROK. Zgranie biblioteki idzie z JEDNEGO automatu (ten sam plik
        na wszystkich jest ten sam, więc pytanie każdego dałoby te same bajty razy liczba maszyn) —
        ale „który jeden" nie jest szczegółem technicznym: świeżo postawiony automat ma katalog
        pusty i to on zawiódł na PESIE. Okno pokazuje zmierzone liczby plików i podpowiada maszynę
        z największą biblioteką.
      */}
      {wybierzZrodlo && d !== undefined && (
        <ZgrajZAutomatu
          dane={d}
          pracuje={zgraj.isPending}
          onZgraj={(agentId) => zgraj.mutate(agentId)}
          onClose={() => setWybierzZrodlo(false)}
        />
      )}

      {/*
        ⚠️ OKNO SYNCHRONIZACJI SAMO CIĄGNIE SWÓJ PLAN i samo pilnuje bramki skanu — ekran pokrycia
        nie musi nic o tym wiedzieć poza tym, że jest co synchronizować.
      */}
      {synchronizuj && <SynchronizujObrazki groupId={groupId} onClose={() => setSynchronizuj(false)} />}

      {doZmiany !== null && (
        <ObrazekTowaru
          towarId={doZmiany.towar.id}
          islandId={groupId}
          numer={doZmiany.towar.itemNumber}
          nazwa={doZmiany.towar.nazwa}
          /** Indeksu dostawcy ekran pokrycia nie zna — podpowiedź do szukania będzie po prostu pusta. */
          indeks={null}
          obecny={doZmiany.towar.imageFile}
          maBajty={doZmiany.wBibliotece}
          onClose={() => {
            setZmieniany(null);
            void dane.refetch();
          }}
        />
      )}
    </Dialog>
  );
}
